<template>
    <div class="tag-videos-container">
        <div class="tag-header">
            <h1 class="tag-title">Videos tagged with #{{ tagName }}</h1>
            <p class="tag-description">Discover videos related to this topic</p>
        </div>

        <div v-if="loading && videos.length === 0" class="shimmer-video-grid">
            <div v-for="n in 8" :key="'shimmer-tag-' + n" class="shimmer-video-card">
                <div class="shimmer-thumb"><div class="shimmer-overlay"></div></div>
                <div class="shimmer-card-info">
                    <div class="shimmer-card-avatar"><div class="shimmer-overlay"></div></div>
                    <div class="shimmer-card-text">
                        <div class="shimmer-card-title"><div class="shimmer-overlay"></div></div>
                        <div class="shimmer-card-title short"><div class="shimmer-overlay"></div></div>
                        <div class="shimmer-card-meta"><div class="shimmer-overlay"></div></div>
                    </div>
                </div>
            </div>
        </div>

        <api-error-state v-else-if="loadError" source="tag_videos" @retry="fetchVideosByTag" />

        <div v-else-if="videos.length === 0" class="no-videos">
            <vue-feather type="video-off" size="48" stroke-width="2"></vue-feather>
            <h3>No videos found</h3>
            <p>No videos are tagged with "{{ tagName }}" yet.</p>
        </div>

        <div v-else class="videos-grid">
            <div 
                v-for="video in videos" 
                :key="video.id"
                class="video-card"
                @click="$router.push(`/watch/${video.id}`)"
            >
                <div class="thumbnail-container">
                    <img 
                        :src="getThumbnailUrl(video)" 
                        :alt="video.titleTA || video.titleEN"
                        class="thumbnail"
                    />
                    <div class="duration">{{ formatDuration(video.duration) }}</div>
                </div>
                <div class="video-info">
                    <h3 class="video-title">{{ decodeHtml(currentLanguage === 'EN' ? (video.titleEN || video.titleTA) : (video.titleTA || video.titleEN)) }}</h3>
                    <div class="video-meta">
                        <span class="views">{{ formatViews(video.viewsCount) }} views</span>
                        <span class="date">{{ formatDate(video.createdAt) }}</span>
                    </div>
                    <div class="channel-info">
                        <img
                            :src="getUserAvatar(getVideoOwner(video))"
                            :alt="displayName(getVideoOwner(video))"
                            class="channel-avatar"
                            @error="handleAvatarError"
                        />
                        <span class="channel-name">
                            {{ decodeHtml(displayName(getVideoOwner(video))) }}
                            <vue-feather v-if="getVideoOwner(video).status === 'verified'" type="check-circle" class="verified-badge" size="12" stroke-width="2"></vue-feather>
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Load More Button -->
        <div v-if="hasMoreVideos && !loading" class="load-more-container">
            <button @click="loadMoreVideos" class="load-more-button">
                Load More Videos
            </button>
        </div>
    </div>
</template>

<script>
import { createSessionPagination } from '../utils/sessionPagination';
import { API } from 'aws-amplify';
import { videosByTagLabel } from '../graphql/custom';
import { captureError } from '../utils/analytics';
import ApiErrorState from './ApiErrorState.vue';

export default {
    name: 'TagVideos',
    components: {
        ApiErrorState
    },
    inject: {
        languageRef: { from: 'currentLanguage', default: 'EN' }
    },
    computed: {
        currentLanguage() {
            return this.languageRef || 'EN';
        }
    },
    data() {
        return {
            videos: [],
            pageProgress: null,
            loading: true,
            loadError: false,
            hasMoreVideos: false,
            nextToken: null,
            tagName: '',
            tagId: null
        }
    },
    async created() {
        this.tagName = this.$route.params.tagName;
        document.title = `#${this.tagName} - Eelam TV`
        await this.fetchVideosByTag();
    },
    watch: {
        '$route.params.tagName': {
            handler(newTagName) {
                this.tagName = newTagName;
                document.title = `#${newTagName} - Eelam TV`
                this.videos = [];
                this.nextToken = null;
                this.fetchVideosByTag();
            }
        },
    },
    methods: {
        async fetchVideosByTag() {
            const progress = createSessionPagination('tag', this.tagName);
            this.pageProgress = progress;
            this.nextToken = null;
            this.hasMoreVideos = false;
            this.tagId = null;
            try {
                this.loading = true;
                this.loadError = false;

                const res = await API.graphql({
                    query: videosByTagLabel,
                    variables: {
                        label: this.tagName,
                        limit: 50
                    }
                });

                if (this.pageProgress !== progress) return;
                const tagItems = res.data.tagByLabel?.items || [];
                if (tagItems.length > 0) {
                    const tag = tagItems[0];
                    const videoJoins = tag.videos?.items || [];
                    this.videos = videoJoins
                        .map(join => join.videoModel)
                        .filter(v => this.isPublicVideo(v));
                    this.nextToken = tag.videos?.nextToken || null;
                    this.hasMoreVideos = !!this.nextToken;
                    this.tagId = tag.id;
                } else {
                    this.videos = [];
                    this.hasMoreVideos = false;
                }

                this.loading = false;
                await progress.restore(() => this.pageProgress === progress && this.loadMoreVideos());
            } catch (error) {
                if (this.pageProgress !== progress) return;
                console.error('Error fetching videos by tag:', error);
                captureError(error, { source: 'api_fetch', component: 'TagVideos', operation: 'fetchVideosByTag', tag: this.tagName });
                this.loadError = true;
            } finally {
                if (this.pageProgress === progress) this.loading = false;
            }
        },

        async loadMoreVideos() {
            if (!this.nextToken || this.loading || !this.tagId) return;
            const progress = this.pageProgress;

            try {
                this.loading = true;

                const res = await API.graphql({
                    query: videosByTagLabel,
                    variables: {
                        label: this.tagName,
                        limit: 50,
                        nextToken: this.nextToken
                    }
                });

                if (this.pageProgress !== progress) return false;
                const tagItems = res.data.tagByLabel?.items || [];
                if (tagItems.length > 0) {
                    const videoJoins = tagItems[0].videos?.items || [];
                    const newVideos = videoJoins
                        .map(join => join.videoModel)
                        .filter(v => this.isPublicVideo(v));
                    this.videos.push(...newVideos);
                    this.nextToken = tagItems[0].videos?.nextToken || null;
                    this.hasMoreVideos = !!this.nextToken;
                    progress.loaded();
                    return true;
                }

            } catch (error) {
                console.error('Error loading more videos:', error);
            } finally {
                if (this.pageProgress === progress) this.loading = false;
            }
        },

        getThumbnailUrl(video) {
            // Use thumbnailUrl from legacy data if available
            if (video.thumbnailUrl) {
                return `https://cdn.eelam.tv/${video.thumbnailUrl}`;
            }
            // Fallback to CDN URL
            return `https://cdn1.eelam.tv/public/thumbs/${video.id}_0.jpeg`;
        },
        isPublicVideo(video) {
            return video &&
                (!video.publishState || video.publishState === 'published') &&
                (!video.status || ['ready', 'processingReady'].includes(video.status));
        },

        getVideoOwner(video) {
            // Return owner if exists, otherwise return dummy owner data
            if (video.owner && video.owner.id) {
                return video.owner;
            }
            // Create dummy owner for videos without an owner
            return {
                id: 'eelam-tv',
                username: 'eelam-tv',
                displayName: 'Eelam TV',
                firstName: 'Eelam',
                lastName: 'TV',
                status: 'unverified',
                emailVerified: false
            };
        },

        getUserAvatar(owner) {
            if (!owner) return '/avatar-placeholder.svg';
            if (owner.legacyAvatar) {
                if (owner.legacyAvatar.startsWith('public/')) {
                    return `https://cdn1.eelam.tv/${owner.legacyAvatar}`;
                }
                return `https://cdn.eelam.tv/${owner.legacyAvatar}`;
            }
            if (!owner.id) return '/avatar-placeholder.svg';
            return `https://cdn1.eelam.tv/public/images/user/${owner.id}/avatar.png`;
        },
        handleAvatarError(event) {
            if (event.target.src.endsWith('/avatar-placeholder.svg')) return;
            event.target.src = '/avatar-placeholder.svg';
        },
        displayName(user) {
            const fullName = [user?.firstName, user?.lastName]
                .filter(Boolean)
                .join(' ')
                .trim();
            return fullName || user?.displayName || user?.username || 'Unknown User';
        },

        formatDuration(seconds) {
            if (!seconds) return '0:00';
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const secs = seconds % 60;
            
            if (hours > 0) {
                return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
            }
            return `${minutes}:${secs.toString().padStart(2, '0')}`;
        },

        formatViews(views) {
            if (!views) return '0';
            if (views >= 1000000) {
                return `${(views / 1000000).toFixed(1)}M`;
            }
            if (views >= 1000) {
                return `${(views / 1000).toFixed(1)}K`;
            }
            return views.toString();
        },

        formatDate(date) {
            if (!date) return '';
            const now = new Date();
            const videoDate = new Date(date);
            const diffDays = Math.floor((now - videoDate) / (1000 * 60 * 60 * 24));
            
            if (diffDays === 0) return 'Today';
            if (diffDays === 1) return 'Yesterday';
            if (diffDays < 7) return `${diffDays} days ago`;
            if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
            if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
            return `${Math.floor(diffDays / 365)} years ago`;
        }
    }
}
</script>

<style scoped>
.tag-videos-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
}

.tag-header {
    text-align: center;
    margin-bottom: 40px;
    padding: 40px 0;
    background: var(--bg-tertiary);
    border-radius: 12px;
}

.tag-title {
    font-size: 32px;
    font-weight: bold;
    color: var(--text-secondary);
    margin-bottom: 10px;
}

.tag-description {
    font-size: 16px;
    color: var(--text-tertiary);
    margin: 0;
}

.shimmer-video-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 24px;
    margin-bottom: 40px;
}

@media (max-width: 768px) {
    .shimmer-video-grid {
        grid-template-columns: 1fr;
        gap: 16px;
    }
}

.no-videos {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 20px;
    color: var(--text-tertiary);
    gap: 16px;
    text-align: center;
}

.no-videos h3 {
    font-size: 24px;
    margin: 0;
    color: var(--text-secondary);
}

.no-videos p {
    font-size: 16px;
    margin: 0;
}

.videos-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 24px;
    margin-bottom: 40px;
}

.video-card {
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    border-radius: 12px;
    overflow: hidden;
    background: var(--bg-card);
    box-shadow: 0 2px 8px var(--shadow-color);
}

.video-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px var(--shadow-dropdown);
}

.thumbnail-container {
    position: relative;
    padding-top: 56.25%;
    overflow: hidden;
}

.thumbnail {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.2s ease;
}

.video-card:hover .thumbnail {
    transform: scale(1.05);
}

.duration {
    position: absolute;
    bottom: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: bold;
}

.video-info {
    padding: 16px;
}

.video-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    color: var(--text-secondary);
    line-height: 1.4;
}

.video-meta {
    display: flex;
    gap: 12px;
    font-size: 14px;
    color: var(--text-tertiary);
    margin-bottom: 12px;
}

.channel-info {
    display: flex;
    align-items: center;
    gap: 8px;
}

.channel-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    object-fit: cover;
}

.channel-name {
    font-size: 14px;
    color: var(--text-tertiary);
}

.verified-badge {
    color: #ff1314;
    margin-left: 3px;
    vertical-align: middle;
}

.load-more-container {
    text-align: center;
    margin-top: 40px;
}

.load-more-button {
    background: var(--accent-primary);
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 25px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.load-more-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* Responsive Design */
@media (max-width: 768px) {
    .tag-videos-container {
        padding: 10px;
    }

    .tag-header {
        padding: 20px;
        margin-bottom: 20px;
    }

    .tag-title {
        font-size: 24px;
    }

    .videos-grid {
        grid-template-columns: 1fr;
        gap: 16px;
    }

    .video-info {
        padding: 12px;
    }
}
</style> 
